import { SpinnerUntil } from "@edgetier/components";
import { CSSProperties, useCallback, useEffect, useMemo, useRef } from "react";
import { usePrevious } from "react-use";
import { IProps } from "./select-selected-list.types";
import { Virtuoso } from "react-virtuoso";
import styles from "../select-menu.module.scss";
import { Text } from "../../../text";
import { Menu } from "../../../menu";
import { Group } from "../../../group";
import { Accordion } from "../../../accordion";
import { calculateGroupedFlatItemIndex } from "../select-menu.utilitites";
import { kebabCase } from "lodash";

/**
 * List of selected items that automatically scrolls to the bottom as each are added.
 * @param props.getGroup             Get a group name for an item.
 * @param props.getLabel             Get a label for an item.
 * @param props.getSelectedItemProps Function to get Downshift props for each item.
 * @param props.getValue             Get a value for an item.
 * @param props.removeSelectedItem   Method to remove an item.
 * @param props.selectedItems        Items chosen by the user.
 * @param props.itemLimit            Optional Maximum number of items that can be selected.
 * @param props.virtualized          Boolean that checks whether the list should be virtualized.
 * @param props.getGroupIcon         Function to get an icon for a group.
 * @returns                          Scrollable list of items.
 */
const SelectSelectedList = <IItem extends {}, IValue extends {}>({
    children,
    getLabel,
    getSelectedItemProps,
    getGroup,
    getValue,
    isLoading,
    removeSelectedItem,
    selectedItems: items,
    virtualized,
    getGroupIcon,
}: IProps<IItem, IValue>) => {
    const element = useRef<HTMLDivElement>(null);

    // Record the count of the selected items to decide whether scrolling needs to happen or not.
    const previousCount = usePrevious(items.length);

    const selectedItems = useMemo(
        () =>
            items.sort((one, two) =>
                // Now they will be sorted by group if any
                typeof getGroup === "function" ? getGroup(one).localeCompare(getGroup(two)) : 0
            ),
        [getGroup, items]
    );

    const { flatItems, flatItemIndexMap, groupedItemsWithIndex } = useMemo(
        () => calculateGroupedFlatItemIndex(selectedItems, getGroup),
        [selectedItems, getGroup]
    );

    // Scroll down when new items are added.
    useEffect(() => {
        if (element.current && typeof previousCount === "number" && items.length > previousCount) {
            element.current.scrollTop = element.current?.scrollHeight;
        }
    }, [items.length, previousCount]);

    const renderItem = useCallback(
        (key: string, style: CSSProperties, index: number, selectedItem: IItem) => {
            return (
                <Menu.Item
                    {...getSelectedItemProps({ selectedItem, index })}
                    onClick={() => removeSelectedItem(selectedItem)}
                    key={key}
                    style={style}
                    aria-label={getLabel(selectedItem)}
                    className={styles["select-menu__option"]}
                    data-testid={`selected-item-${kebabCase(getLabel(selectedItem))}`}
                >
                    <Group>
                        <Text
                            size="sm"
                            className={styles["select-menu__option__label"]}
                            data-testid={`selected-item-label-${kebabCase(getLabel(selectedItem))}`}
                        >
                            {typeof children === "function" ? children(selectedItem) : getLabel(selectedItem)}
                        </Text>
                    </Group>
                </Menu.Item>
            );
        },
        [getSelectedItemProps, getLabel, children, removeSelectedItem]
    );

    return (
        <div className={`${styles["select-menu__list"]} ${styles["select-menu__list--is-selected"]}`}>
            <div className={styles["select-menu__options"]} ref={element}>
                <SpinnerUntil data={[]} isReady={!isLoading}>
                    {virtualized ? (
                        <ul data-testid="selected-items">
                            <Virtuoso
                                data={selectedItems}
                                itemContent={(index) => {
                                    const selectedItem = selectedItems[index];
                                    const value = getValue(selectedItem);
                                    const key =
                                        typeof value === "object" ? Object.values(value).join() : value.toString();

                                    return renderItem(key, {}, index, selectedItem);
                                }}
                            />
                        </ul>
                    ) : (
                        <ul data-testid="selected-items">
                            {typeof getGroup === "function" ? (
                                <Accordion>
                                    {Object.entries(groupedItemsWithIndex).map(([group, items]) => (
                                        <Accordion.Item key={group} value={group} bd="none">
                                            <Accordion.Control
                                                className={styles["select-menu__accordion__control"]}
                                                icon={getGroupIcon?.(group) ?? undefined}
                                                data-testid={`selected-items-group-${kebabCase(group)}`}
                                            >
                                                <Text
                                                    span
                                                    size="sm"
                                                    data-testid={`selected-items-group-label-${kebabCase(group)}`}
                                                >
                                                    {group}
                                                </Text>
                                            </Accordion.Control>
                                            <Accordion.Panel pl="xxl">
                                                {items.map(({ item: selectedItem }, index) => {
                                                    const value = getValue(selectedItem);
                                                    const key =
                                                        typeof value === "object"
                                                            ? Object.values(value).join()
                                                            : value.toString();
                                                    const flatItemIndex = flatItemIndexMap.get(selectedItem);

                                                    return renderItem(key, {}, flatItemIndex ?? index, selectedItem);
                                                })}
                                            </Accordion.Panel>
                                        </Accordion.Item>
                                    ))}
                                </Accordion>
                            ) : (
                                selectedItems.map((selectedItem, index) => {
                                    const value = getValue(selectedItem);
                                    const key =
                                        typeof value === "object" ? Object.values(value).join() : value.toString();

                                    return renderItem(key, {}, index, selectedItem);
                                })
                            )}
                        </ul>
                    )}
                </SpinnerUntil>
            </div>
        </div>
    );
};

export default SelectSelectedList;
